import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Modal, Select, Input, Button, Toggle } from 'common/components';
import { useCreateTenant } from 'services/hooks/mutations';
import { CreateTenantModalProps, TenantForm } from './CreateTenantModal.types';
import { useGetTwillioActiveAccounts, useGetOutboundTrunks } from 'services/hooks/queries';
import styles from './CreateTenantModal.module.scss';

export const CreateTenantModal: React.FC<CreateTenantModalProps> = ({ isOpen, onClose, onSuccess, onError }) => {
    const [useExistingAccounts, setUseExistingAccounts] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        control,
        setValue,
    } = useForm<TenantForm>({
        defaultValues: {
            name: '',
            outboundTrunkSid: '',
            phoneNumber: '',
            transferNumber: '',
            twilioSubAccountSID: '',
            crmPortal: '',
            crmClientId: '',
            crmClientSecret: '',
            crmUsername: '',
            crmPassword: '',
        },
    });

    const { twilioActiveAccounts, isTwilioActiveAccountsLoading } = useGetTwillioActiveAccounts(
        isOpen && useExistingAccounts
    );
    const { createTenant, isPending } = useCreateTenant();
    const { outboundTrunks, isOutboundTrunksLoading } = useGetOutboundTrunks(isOpen && useExistingAccounts);

    const handleTrunkSelect = (trunkId: string) => {
        const selectedTrunk = outboundTrunks?.find((trunk) => trunk.trunk_id === trunkId);
        if (selectedTrunk) {
            setValue('outboundTrunkSid', selectedTrunk.trunk_id);
            setValue('phoneNumber', selectedTrunk.phone_numbers[0] || '');
            setValue('transferNumber', '+15555555555');
        }
    };

    const onSubmit = async (data: TenantForm) => {
        // Construct tenant config with defaults
        const tenantConfig = {
            status: 'ACTIVE',
            dashboardData: {
                infos: ['hotLeads'],
                charts: [
                    'callDistributionBarGraph',
                    'callStatusDistributionBar',
                    'salesFunnelArea',
                    'callTimeDistributionBar',
                    'callQualificationDistributionBar',
                    'callTransferDistributionBar',
                ],
                metrics: [],
            },
            businessHours: {
                startTime: '00:00',
                endTime: '23:59',
            },
        } as any;

        // Add optional CRM credentials if any field is filled
        if (data.crmPortal || data.crmClientId || data.crmClientSecret || data.crmUsername || data.crmPassword) {
            tenantConfig.crmCredentials = {
                lawruler: {
                    ...(data.crmPortal && { portal: data.crmPortal }),
                    ...(data.crmClientId && { clientId: data.crmClientId }),
                    ...(data.crmClientSecret && { clientSecret: data.crmClientSecret }),
                    ...(data.crmUsername && { username: data.crmUsername }),
                    ...(data.crmPassword && { password: data.crmPassword }),
                },
            };
        }

        // Construct the full tenant data object
        const tenantData: any = {
            name: data.name,
            tenantConfig: tenantConfig,
        };

        // Only include Twilio and LiveKit fields if using existing accounts
        if (useExistingAccounts) {
            tenantData.twilioSubAccountSID = data.twilioSubAccountSID;
            tenantData.outboundTrunkSid = data.outboundTrunkSid;
            tenantData.phoneNumber = data.phoneNumber;
            tenantData.transferNumber = data.transferNumber;
        }

        try {
            // Call API to create the tenant
            const response = await createTenant(tenantData);

            if (response) {
                onSuccess(response);
                reset();
                setUseExistingAccounts(false);
                onClose();
            }
        } catch (error: any) {
            console.error('Error creating tenant:', error);
            onError(error.response?.data?.message || 'Failed to create tenant. Please try again.');
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title='Create New Tenant' size='sm' showDefaultFooter={false}>
            <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-md'>
                <Input
                    label='Tenant Name'
                    type='text'
                    {...register('name', { required: 'Tenant name is required' })}
                    error={!!errors.name}
                    errorText={errors.name?.message}
                    required
                />

                <div className={styles.toggleContainer}>
                    <span className={styles.toggleLabel}>Create from scratch</span>
                    <Toggle checked={useExistingAccounts} onChange={(e) => setUseExistingAccounts(e.target.checked)} />
                    <span className={styles.toggleLabel}>Accounts already created</span>
                </div>

                {useExistingAccounts && (
                    <>
                        <div className={styles.sectionHeader}>LiveKit Trunk *</div>

                        {isOutboundTrunksLoading ? (
                            <div style={{ padding: '10px', textAlign: 'center' }}>Loading outbound trunks...</div>
                        ) : (
                            <Controller
                                name='outboundTrunkSid'
                                control={control}
                                rules={{ required: useExistingAccounts ? 'LiveKit Trunk is required' : false }}
                                render={({ field }) => (
                                    <Select
                                        label='Select LiveKit Trunk *'
                                        options={[
                                            { value: '', label: 'Select a LiveKit Trunk', disabled: true },
                                            ...(outboundTrunks || []).map((trunk) => ({
                                                value: trunk.trunk_id,
                                                label: trunk.name,
                                            })),
                                        ]}
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            handleTrunkSelect(e.target.value);
                                        }}
                                    />
                                )}
                            />
                        )}

                        <div className={styles.sectionHeader}>Twilio Account *</div>

                        {isTwilioActiveAccountsLoading ? (
                            <div style={{ padding: '10px', textAlign: 'center' }}>Loading accounts...</div>
                        ) : (
                            <Controller
                                name='twilioSubAccountSID'
                                control={control}
                                rules={{ required: useExistingAccounts ? 'Twilio Account is required' : false }}
                                render={({ field }) => (
                                    <Select
                                        label='Select Twilio Account *'
                                        options={[
                                            { value: '', label: 'Select a Twilio Account', disabled: true },
                                            ...(twilioActiveAccounts || []).map((account) => ({
                                                value: account.sid,
                                                label: account.friendlyName,
                                            })),
                                        ]}
                                        {...field}
                                    />
                                )}
                            />
                        )}
                    </>
                )}

                <div className={styles.sectionHeader}>CRM Credentials (Optional - LawRuler)</div>

                <Input label='Portal' type='text' {...register('crmPortal')} />

                <Input label='Client ID' type='text' {...register('crmClientId')} />

                <Input label='Client Secret' type='password' showPasswordToggle {...register('crmClientSecret')} />

                <Input label='Username' type='text' {...register('crmUsername')} />

                <Input label='Password' type='password' showPasswordToggle {...register('crmPassword')} />

                <div className='flex justify-end gap-md mt-xl'>
                    <Button onClick={onClose} variant='secondary'>
                        Cancel
                    </Button>
                    <Button type='submit' disabled={isPending}>
                        Create Tenant
                    </Button>
                </div>
            </form>
        </Modal>
    );
};
